import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CompanyManagement.css';
import logger from './utils/logger';

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModal, setCreateModal] = useState({ open: false, name: '' });
  const [editModal, setEditModal] = useState({ open: false, id: null, name: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/companies');
      setCompanies(response.data);
      setError(null);
    } catch (err) {
      logger.apiError('GET', '/api/companies', err);
      setError(err.response?.data?.error || '분류 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    const { name } = createModal;
    
    if (!name || !name.trim()) {
      alert('분류명을 입력해주세요.');
      return;
    }

    try {
      setCreateLoading(true);
      // 권한은 서버가 토큰으로 판정한다.
      await axios.post('/api/companies', { name: name.trim() });


      alert('분류가 생성되었습니다.');
      setCreateModal({ open: false, name: '' });
      fetchCompanies(); // 목록 새로고침
    } catch (err) {
      logger.userAction('분류 생성', { name }, err);
      alert(err.response?.data?.error || '분류 생성에 실패했습니다.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditCompany = async () => {
    if (!editModal.name || !editModal.name.trim()) {
      alert('분류명을 입력해주세요.');
      return;
    }

    try {
      setEditLoading(true);
      await axios.put(`/api/companies/${editModal.id}`, {
        name: editModal.name.trim()
      });


      alert('분류가 수정되었습니다.');
      setEditModal({ open: false, id: null, name: '' });
      fetchCompanies(); // 목록 새로고침
    } catch (err) {
      logger.userAction('분류 수정', { id: editModal.id }, err);
      alert(err.response?.data?.error || '분류 수정에 실패했습니다.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm(
      '이 분류를 삭제하면 일별정산·분류별 요율·월이용료·유보금 등\n' +
      '해당 분류에 묶인 데이터가 삭제됩니다. (사용자 계정은 삭제되지 않습니다.)\n' +
      '정말 삭제하시겠습니까?'
    )) {
      return;
    }

    try {
      await axios.delete(`/api/companies/${id}`);

      alert('분류가 삭제되었습니다.');
      fetchCompanies(); // 목록 새로고침
    } catch (err) {
      logger.userAction('분류 삭제', { id }, err);
      alert(err.response?.data?.error || '분류 삭제에 실패했습니다.');
    }
  };

  const openCreateModal = () => {
    setCreateModal({ open: true, name: '' });
  };

  const closeCreateModal = () => {
    setCreateModal({ open: false, name: '' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // UTC 시간에 9시간을 더해서 한국시간으로 변환
    const koreanTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    return koreanTime.toLocaleString('ko-KR');
  };

  const openEditModal = (company) => {
    setEditModal({ open: true, id: company.id, name: company.name });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, id: null, name: '' });
  };

  if (loading) {
    return (
      <div className="company-management-container">
        <div className="loading">분류 목록을 불러오는 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-management-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="company-management-container">
      <div className="header-section">
        <div>
          <h2>분류 관리</h2>
          <p className="description">
            {currentUser?.role === 'super' 
              ? '슈퍼어드민은 모든 분류를 관리할 수 있습니다.' 
              : currentUser?.role === 'admin'
              ? '본사는 분류를 직접 관리할 수 있습니다.'
              : '분류 목록을 확인할 수 있습니다.'}
          </p>
        </div>
        {['super', 'admin'].includes(currentUser?.role) && (
          <button className="create-company-btn" onClick={openCreateModal}>
            + 새 분류 추가
          </button>
        )}
      </div>
      
      <div className="company-table-container">
        <table className="company-table">
          <thead>
            <tr>
              <th>번호</th>
              <th>분류명</th>
              <th>등록일</th>
              {['super', 'admin'].includes(currentUser?.role) && <th>관리</th>}
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 ? (
              <tr>
                <td colSpan={['super', 'admin'].includes(currentUser?.role) ? 4 : 3} className="no-data">
                  분류가 없습니다.
                </td>
              </tr>
            ) : (
              companies.map((company) => (
                <tr key={company.id}>
                  <td>{company.id}</td>
                  <td>{company.name}</td>
                  <td>{formatDate(company.created_at)}</td>
                  {['super', 'admin'].includes(currentUser?.role) && (
                    <td>
                      <div className="action-buttons">
                        <button
                          className="approve-btn"
                          onClick={() => openEditModal(company)}
                        >
                          수정
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteCompany(company.id)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 분류 생성 모달 */}
      {createModal.open && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>새 분류 추가</h3>
              <button className="modal-close" onClick={closeCreateModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>분류명</label>
                <input
                  type="text"
                  value={createModal.name}
                  onChange={e => setCreateModal({ ...createModal, name: e.target.value })}
                  placeholder="분류명을 입력하세요"
                  disabled={createLoading}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-cancel" 
                onClick={closeCreateModal}
                disabled={createLoading}
              >
                취소
              </button>
              <button 
                className="btn-confirm" 
                onClick={handleCreateCompany}
                disabled={createLoading}
              >
                {createLoading ? '처리 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 분류 수정 모달 */}
      {editModal.open && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>분류 수정</h3>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>분류명</label>
                <input
                  type="text"
                  value={editModal.name}
                  onChange={e => setEditModal({ ...editModal, name: e.target.value })}
                  placeholder="분류명을 입력하세요"
                  disabled={editLoading}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-cancel" 
                onClick={closeEditModal}
                disabled={editLoading}
              >
                취소
              </button>
              <button 
                className="btn-confirm" 
                onClick={handleEditCompany}
                disabled={editLoading}
              >
                {editLoading ? '처리 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyManagement; 